import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toRef, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

import type { ListUsersResponse, UserOOO } from '@stuut/api-sdk/models';
import type {
  PatchUsersUserIdOooRequest,
  PostUsersUserIdOooRequest,
} from '@stuut/api-sdk/models/operations';

import { sdk } from '@/sdk';

import { USER_KEYS } from './useUsers';

export const OOO_KEYS = {
  all: ['user-ooo'] as const,
  byUser: (userId: string) => ['user-ooo', 'by-user', userId] as const,
  preferencesByUser: (userId: string) => ['user-ooo', 'preferences', userId] as const,
};

export type CreateOOOPayload = PostUsersUserIdOooRequest;
export type UpdateOOOPayload = PatchUsersUserIdOooRequest;

const fetchOutOfOffice = async (userId: string): Promise<UserOOO | null> => {
  const res = await sdk.usersOoo.getOOO({ userId });
  return res.ooo ?? null;
};

const OOO_QUERY_STALE_TIME = 60 * 1000;

const outOfOfficeQueryOptions = (userId: string) => ({
  enabled: !!userId,
  queryFn: () => fetchOutOfOffice(userId),
  queryKey: OOO_KEYS.byUser(userId),
  staleTime: OOO_QUERY_STALE_TIME,
});

export function useOutOfOfficeQuery(
  userId: MaybeRefOrGetter<string | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const userIdRef = toRef(userId);
  const enabledRef = options.enabled ? toRef(options.enabled) : undefined;
  return useQuery({
    enabled: computed(() => !!userIdRef.value && (enabledRef ? enabledRef.value : true)),
    queryFn: () => fetchOutOfOffice(userIdRef.value as string),
    queryKey: computed(() => OOO_KEYS.byUser(userIdRef.value ?? '')),
    staleTime: OOO_QUERY_STALE_TIME,
  });
}

export function useOutOfOfficeQueries(userIds: MaybeRefOrGetter<string[]>) {
  return useQueries({
    queries: computed(() => toValue(userIds).map(outOfOfficeQueryOptions)),
  });
}

export function useOutOfOfficePreferencesQuery(userId: MaybeRefOrGetter<string | undefined>) {
  const userIdRef = toRef(userId);
  return useQuery({
    enabled: computed(() => !!userIdRef.value),
    queryFn: () => sdk.usersOoo.getOOOPreferences({ userId: userIdRef.value as string }),
    queryKey: computed(() => OOO_KEYS.preferencesByUser(userIdRef.value ?? '')),
  });
}

type UserListPatch = Partial<Pick<ListUsersResponse['data'][number], 'isOoo' | 'backupUserId'>>;

function patchUserInLists(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  patch: UserListPatch,
) {
  queryClient.setQueriesData<ListUsersResponse>({ queryKey: USER_KEYS.all }, (existing) => {
    if (!existing) return existing;
    let changed = false;
    const data = existing.data.map((user) => {
      if (user.id !== userId) return user;
      changed = true;
      return { ...user, ...patch };
    });
    return changed ? { ...existing, data } : existing;
  });
}

export function useCreateOutOfOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOOOPayload) => sdk.usersOoo.createOOO(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: OOO_KEYS.byUser(payload.userId) });
      patchUserInLists(queryClient, payload.userId, {
        backupUserId: payload.body.backupUserId,
        isOoo: true,
      });
    },
  });
}

export function useUpdateOutOfOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOOOPayload) => sdk.usersOoo.updateOOO(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: OOO_KEYS.byUser(payload.userId) });
      const patch: UserListPatch = { isOoo: true };
      if (payload.body.backupUserId !== undefined) patch.backupUserId = payload.body.backupUserId;
      patchUserInLists(queryClient, payload.userId, patch);
    },
  });
}

export function useDeleteOutOfOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: string }) => sdk.usersOoo.deleteOOO({ userId: vars.userId }),
    onSuccess: (_data, vars) => {
      queryClient.setQueryData(OOO_KEYS.byUser(vars.userId), null);
      patchUserInLists(queryClient, vars.userId, { backupUserId: null, isOoo: false });
    },
  });
}
